<script setup lang="ts">
import { computed, ref, type PropType } from 'vue';
import {
  PARTY_KIND_LABELS,
  type ContractualParty,
} from '@/services/contractManagement/contractualPartyRow';
import { contractualPartyRoute } from '@/router/links';
import { ACCESS_PERMISSION_WRITE, deniedReason } from '@/utils/accessPermission';
import GaiaAddButton from '../../base/GaiaAddButton.vue';
import GaiaDeleteButton from '../../base/GaiaDeleteButton.vue';
import GaiaText from '../../base/GaiaText.vue';
import GaiaPopconfirm from '../../composite/GaiaPopconfirm.vue';
import { gaiaNeutral } from '../../themes/tokens';

const props = defineProps({
  parties: {
    type: Array as PropType<ContractualParty[]>,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  /** Why the caller can't link or unlink parties on this file; null when they can. */
  writeBlock: {
    type: String as PropType<string | null>,
    default: null,
  },
});

const emit = defineEmits<{ add: []; remove: [party: ContractualParty] }>();

/** Chips shown before "+N more". */
const LIMIT = 4;
const expanded = ref(false);

const shown = computed(() => (expanded.value ? props.parties : props.parties.slice(0, LIMIT)));

function metaOf(party: ContractualParty): string {
  return [PARTY_KIND_LABELS[party.kind], party.city, party.country_name]
    .filter(Boolean)
    .join(' · ');
}

/**
 * Why this party can't be unlinked: the file's write gate, then the party's
 * own — the link is part of the party too.
 */
function unlinkBlock(party: ContractualParty): string | null {
  return props.writeBlock ?? deniedReason(party.permissions, ACCESS_PERMISSION_WRITE);
}

const chipBorder = gaiaNeutral.border;
const chipHover = gaiaNeutral.surfaceHover;
const moreBg = gaiaNeutral.surfaceTint;
</script>

<template>
  <!-- The Contractual Party row of the Contract View mock: one chip per party
       (name, kind and place), "+N more" past four, and the way to link one. -->
  <n-flex align="center" :size="6">
    <gaia-text v-if="props.loading" size="dense" tone="faint">Loading…</gaia-text>
    <template v-else>
      <span v-for="party in shown" :key="party.uid" class="chip">
        <router-link :to="contractualPartyRoute(party.uid)" class="chip-link">
          <gaia-text size="dense" weight="semibold" tone="ink">{{ party.name }}</gaia-text>
          <gaia-text v-if="metaOf(party)" size="micro" tone="subtle">{{ metaOf(party) }}</gaia-text>
        </router-link>
        <gaia-popconfirm
          :title="`Unlink “${party.name}”?`"
          confirm-label="Unlink"
          @confirm="emit('remove', party)"
        >
          <template #trigger>
            <gaia-delete-button
              intent="remove"
              size="xs"
              subject="from this file"
              :disabled="Boolean(unlinkBlock(party))"
              :title="unlinkBlock(party) ?? undefined"
            />
          </template>
          The party itself stays — only its link to this file is removed.
        </gaia-popconfirm>
      </span>
      <button
        v-if="props.parties.length > LIMIT"
        type="button"
        class="more"
        @click="expanded = !expanded"
      >
        <gaia-text size="dense" weight="semibold" tone="ink">
          {{ expanded ? 'Show fewer' : `+${props.parties.length - LIMIT} more` }}
        </gaia-text>
      </button>
      <gaia-add-button
        size="xs"
        variant="dashed"
        subject="party"
        :disabled="Boolean(props.writeBlock)"
        :title="props.writeBlock ?? ''"
        @click="emit('add')"
      />
    </template>
  </n-flex>
</template>

<style scoped>
.chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 26px;
  padding: 0 4px 0 10px;
  border: 1px solid v-bind(chipBorder);
  border-radius: 8px;
  white-space: nowrap;
}

.chip:hover {
  background: v-bind(chipHover);
}

.chip-link {
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  text-decoration: none;
}

.more {
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 10px;
  border: none;
  border-radius: 8px;
  background: v-bind(moreBg);
  cursor: pointer;
}
</style>
